import { Injectable } from '@angular/core';
import { CalendarMonth, DateRange } from './date.model';

@Injectable({
    providedIn: 'root'
})
export class DateRangeService {
	
	/**
	 * Returns the date range for the current month.
	 * 
	 * The range starts on the first day of the month containing `rangeEndDate`
	 * and ends on `rangeEndDate`. If `rangeEndDate` is not provided the current date is used.
	 * @param rangeEndDate Optional end date of the range (defaults to today).
	 * @returns DateRange with startDate as the first of the month and endDate as the reference date.
	 */
    getCurrentMonth(rangeEndDate?: Date | null): DateRange {
		const endDate = rangeEndDate ?? new Date();
		const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

		return {startDate, endDate};
	}

	/**
	 * Returns a date range representing a single day.
	 * 
	 * Both startDate and endDate are set to the provided date. 
	 * If no date is provided, the current date is used.
	 * @param date 
	 * @returns 
	 */
	getSingleDate(date?: Date | null): DateRange {
		const rangeDate = date ?? new Date();
		return {
			startDate: rangeDate,
			endDate: rangeDate
		}
	}


	/**
	 * Returns the year-to-date range.
	 * 
	 * The range starts on the first day of the year defined by `yearStartMonth`
	 * and ends on `rangeEndDate`. If `yearStartMonth` is not provided, January is used, which represents standard calendar Year-To-Date.
	 * If `rangeEndDate` is not provided, the current date is used.
	 * 
	 * @param rangeEndDate Optional end date of the range (defaults to today).
	 * @param yearStartMonth Optional starting month of the year (e.g., fiscal year).
	 * @returns DateRange representing the calculated year-to-date range.
	 */
	getYearToDate(rangeEndDate?: Date | null, yearStartMonth?: CalendarMonth | null): DateRange {
		const endDate = rangeEndDate ?? new Date();

		const startMonth = yearStartMonth != null ? yearStartMonth as number - 1 : 0;
		const startYear = endDate.getMonth() < startMonth ? endDate.getFullYear() - 1 : endDate.getFullYear();

		const startDate = new Date(startYear, startMonth, 1);
		
		return {startDate, endDate};
	}


	/**
	 * Returns a rolling date range for the last X periods.
	 * 
	 * The range ends on `rangeEndDate` (or today if not provided).
	 * The start date is calculated by sutracting `periodCount` units (day, month, or year) from the end date.
	 * 
	 * For days, the range is inclusive of the end date.
	 * For months and years, the range rolls back by full units from the end date.
	 * 
	 * @param periodUnit Unit time ('day' | 'month' | 'year').
	 * @param periodCount Number of units to subtract.
	 * @param rangeEndDate Optional end date (defaults to today).
	 * @returns DateRange representing the calculated rolling range.
	 */
	getLastXPeriods(periodUnit: 'day' | 'month' | 'year', periodCount: number, rangeEndDate?: Date | null): DateRange {
		const endDate = rangeEndDate ?? new Date();
		const startDate = new Date(endDate);

		switch (periodUnit) {
			case 'day':
				startDate.setDate(startDate.getDate() - (periodCount - 1)); // X Number of days including the end date
				break;
			case 'month': 
				startDate.setMonth(startDate.getMonth() - periodCount);
				break;
			case 'year': 
				startDate.setFullYear(startDate.getFullYear() - periodCount);
				break;
		}

		return {startDate, endDate};	
	}
}
